import { useEffect, useRef, useState } from "react";
import { LuLoaderCircle, LuTrash2 } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

// Feature flag: Enable when backend DELETE /chat/delete/:projectId is ready
const DELETE_PROJECT_ENABLED = false;
import { ChatApi } from "../../../api/chatApi";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import {
  useProjectsListActions,
  useProjectsMatchingSearchQuery,
} from "../../../core_stores/useProjectsListStore";
import {
  SideMenuBehavior,
  useScrollToTopTrigger,
  useSideMenuActions,
  useSideMenuBehavior,
  useSideMenuSelectedProjectId,
} from "../../../view_stores/useSideMenuStore";
import SideMenuProjectDeleteConfirmation from "./SideMenuProjectDeleteConfirmation";
import { typography } from "../../../design/tokens";

export default function SideMenuProjects() {
  const projectsMatchingSearchQuery = useProjectsMatchingSearchQuery();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollToTopTrigger = useScrollToTopTrigger();

  useEffect(() => {
    if (scrollToTopTrigger > 0 && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [scrollToTopTrigger]);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <h2 className={`${typography.size.sm} font-medium text-light-text dark:text-dark-text mb-2`}>
        Your Projects
      </h2>
      <div ref={scrollContainerRef} className="flex-1 space-y-1 overflow-y-auto pb-6">
        {projectsMatchingSearchQuery === null ? (
          <div className="flex justify-center items-center mt-4">
            <LuLoaderCircle className="animate-spin" size={24} />
          </div>
        ) : projectsMatchingSearchQuery.length > 0 ? (
          <ProjectsList />
        ) : (
          <div className="text-sm text-gray-400">No projects found</div>
        )}
      </div>
    </div>
  );
}

function ProjectsList() {
  const projectsMatchingSearchQuery = useProjectsMatchingSearchQuery();
  const selectedProjectId = useSideMenuSelectedProjectId();
  const sideMenuBehavior = useSideMenuBehavior();
  const isCompactView = useIsCompactView();
  const navigate = useNavigate();

  // Delete modal state
  const [deleteModalProject, setDeleteModalProject] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Long-press state for mobile
  const [longPressProjectId, setLongPressProjectId] = useState<string | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    setSelectedProjectId,
    close: closeSideMenu,
  } = useSideMenuActions();

  const projectsListActions = useProjectsListActions();

  // Clear long-press timer
  const clearLongPressTimer = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Handle touch start for long-press
  const handleTouchStart = (projectId: string) => {
    if (!isCompactView) return;
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      setLongPressProjectId(projectId);
    }, 500);
  };

  // Handle touch end/move to cancel long-press
  const handleTouchEnd = () => {
    clearLongPressTimer();
  };

  // Handle delete button click
  const handleDeleteClick = (
    e: React.MouseEvent,
    projectId: string,
    projectName: string
  ) => {
    e.stopPropagation();
    setDeleteModalProject({ id: projectId, name: projectName });
    setLongPressProjectId(null);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteModalProject) return;

    const projectIdToDelete = deleteModalProject.id;
    setDeleteModalProject(null);

    // Call delete API
    await ChatApi.deleteProject(projectIdToDelete);

    // Remove from local store
    projectsListActions.deleteProject(projectIdToDelete);

    // If user is viewing the deleted project, navigate to home
    if (selectedProjectId === projectIdToDelete) {
      navigate("/");
    }
  };

  function handleProjectClick(projectId: string) {
    if (projectId === selectedProjectId) {
      return;
    }
    setSelectedProjectId(projectId);
    if (sideMenuBehavior === SideMenuBehavior.AUTO) {
      window.dispatchEvent(new Event("darvin-block-reopen-menu"));
      closeSideMenu();
    }

    // Find project in list to get cached name for optimistic display
    const project = projectsMatchingSearchQuery?.find((p) => p.id === projectId);

    navigate(`/project/${projectId}`, {
      state: {
        projectId: projectId,
        projectName: project?.name || null,
      },
    });
  }

  return (
    <>
      {projectsMatchingSearchQuery!.map((project) => (
        <div
          key={project.id}
          data-project-id={project.id}
          className={`side-menu-project-item ${
            selectedProjectId === project.id
              ? "bg-[#B4B7CC] dark:bg-[#2A2E34] text-white"
              : "text-light-text dark:text-dark-text hover:bg-light-hover dark:hover:bg-dark-hover"
          } p-2 rounded-lg cursor-pointer ${typography.size.sm}
          mr-2 transition-colors flex items-center justify-between group`}
          onClick={() => handleProjectClick(project.id)}
          onTouchStart={() => handleTouchStart(project.id)}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchEnd}
        >
          <span className="flex-1 truncate">{project.name ?? project.id}</span>

          {/* Desktop: show on hover */}
          {DELETE_PROJECT_ENABLED && !isCompactView && (
            <button
              className={`opacity-0 group-hover:opacity-100 p-1 rounded
                hover:bg-white/10 transition-opacity
                ${
                  selectedProjectId === project.id
                    ? "text-white"
                    : "text-light-text dark:text-dark-text"
                }`}
              onClick={(e) =>
                handleDeleteClick(e, project.id, project.name ?? project.id)
              }
            >
              <LuTrash2 size={16} />
            </button>
          )}

          {/* Mobile: show after long-press */}
          {DELETE_PROJECT_ENABLED && isCompactView && longPressProjectId === project.id && (
            <button
              className="p-1 rounded bg-white/10 animate-fade-in text-white"
              onClick={(e) =>
                handleDeleteClick(e, project.id, project.name ?? project.id)
              }
            >
              <LuTrash2 size={16} />
            </button>
          )}
        </div>
      ))}

      {/* Delete confirmation modal */}
      {deleteModalProject && (
        <SideMenuProjectDeleteConfirmation
          projectName={deleteModalProject.name}
          onClose={() => setDeleteModalProject(null)}
          onDelete={handleDeleteConfirm}
        />
      )}
    </>
  );
}
